import type { ChangeEvent } from "react";
import { getLanguageDisplayName, type LanguageLike } from "@/utils/languageUtils";

export type ModalLanguageOption = LanguageLike & {
  id?: number | string;
  languageId?: number | string;
  key?: string;
  languageName?: string;
};

type ModalLanguageSelectorProps = {
  languages: ModalLanguageOption[];
  value: number | string | null | undefined;
  onChange: (languageId: string) => void;
  selectId?: string;
  disabled?: boolean;
  label?: string;
  title?: string;
  placement?: "header" | "footer";
  getOptionLabel?: (language: ModalLanguageOption) => string;
  getOptionValue?: (language: ModalLanguageOption) => string;
};

export default function ModalLanguageSelector({
  languages,
  value,
  onChange,
  selectId = "modalLanguageSelect",
  disabled = false,
  label = "Language",
  title = "Select language to edit",
  placement = "header",
  getOptionLabel,
  getOptionValue,
}: ModalLanguageSelectorProps) {
  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    onChange(event.target.value);
  }

  const wrapperClassName = placement === "footer" ? "footerLanguageSelector" : "headerLanguageSelector";

  return (
    <div className={wrapperClassName} data-no-drag="true">
      <label htmlFor={selectId} className="modalLanguageSelectorLabel">
        {label}
      </label>
      <select
        id={selectId}
        className="modalLanguageSelectorSelect"
        value={value ?? ""}
        onChange={handleChange}
        disabled={disabled}
        title={title}
      >
        {languages.map((language) => {
          const optionValue = getOptionValue
            ? getOptionValue(language)
            : String(language.id ?? language.languageId ?? language.key ?? "");
          const optionLabel = getOptionLabel
            ? getOptionLabel(language)
            : (language.languageName || getLanguageDisplayName(language));
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
}
